import React, {useEffect, useMemo} from 'react';
import {useAllTaskListSelector, useTaskSnapshotsSelector} from "../../../../store/selectors/task/task.selectors";
import {subsManager} from "../../../../store/middleware/subs-manager/subs-manager";
import {
    useActiveProjectCalendarsSelector,
    useActiveProjectSelector,
    useCpmMapSelector,
    useDataVersionsSelector
} from "../../../../store/selectors/project.selectors";
import {scaleTime} from "@visx/scale";
import {extent, bisector} from '@visx/vendor/d3-array'
import {LinePath} from "@visx/shape";
import {GridColumns, GridRows} from "@visx/grid";
import {AxisLeft} from "@visx/axis";
import moment from "moment";
import {convertIndexToSeconds} from "../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import {CpmTaskModel} from "../../../../models/responses/cpm-task.model";

const margin = {top: 10, right: 10, bottom: 10, left: 10};
const accentColor = '#ed6c02';
const lineColor = '#4285f4';

const bisectDate = bisector((d: any) => d.date).left;

type Datum = { date: Date; value: Date };

function linearRegressionEndpoints(data: Datum[]) {
    const pts = data.map(d => [d.date.getTime(), d.value.getTime()] as const);
    const n = pts.length;
    if (n < 2) return null;

    const sumX = pts.reduce((s, [x]) => s + x, 0);
    const sumY = pts.reduce((s, [, y]) => s + y, 0);
    const sumXY = pts.reduce((s, [x, y]) => s + x * y, 0);
    const sumXX = pts.reduce((s, [x]) => s + x * x, 0);

    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) return null; // vertical or degenerate

    const m = (n * sumXY - sumX * sumY) / denom; // slope
    const b = (sumY - m * sumX) / n;             // intercept

    const xs = pts.map(([x]) => x);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);

    const yMin = m * xMin + b;
    const yMax = m * xMax + b;

    return [
        { date: new Date(xMin), value: new Date(yMin) },
        { date: new Date(xMax), value: new Date(yMax) },
    ] as Datum[];
}



export default function DateChart(props: {
    taskId: string,
    baselineDate: Date,
    width: number,
    height: number
}) {
    const {taskId, width, height, baselineDate} = props;
    const taskSnapshots = useTaskSnapshotsSelector();
    const activeProject = useActiveProjectSelector();
    const allTasks = useAllTaskListSelector();
    const dataVersions = useDataVersionsSelector();
    const task = allTasks.get(taskId);
    const taskCode = task ? task.task_code : null;
    const [hoverPosition, setHoverPosition] = React.useState<{ x: number; y: number } | null>(null);
    const cpmMap: Map<string, CpmTaskModel> = useCpmMapSelector();
    const calendars = useActiveProjectCalendarsSelector();
    // const [mouseIsOver, setMouseIsOver] = React.useState(false);

    useEffect(() => {
        if (taskCode) {
            const dataVersionMap = dataVersions.reduce((map, obj) => {
                map.set(obj.dataVersionId, obj.dataVersionDate.seconds);
                return map;
            }, new Map<string, {code: string, version: number}>());
            subsManager.subscribeTaskSnapshots(taskCode, activeProject ? activeProject.contractId : '', dataVersionMap);
        }
    }, [taskId]);

    const data = useMemo(() => {
        if (!activeProject) return [];
        if (activeProject.projectId !== task?.projectId) return [];
        if (taskSnapshots.length && task) {
            let snapshots = taskSnapshots.map(snapshot => ({
                date: new Date(snapshot.timestamp! * 1000),
                value: snapshot.actEndDate ? snapshot.actEndDate.toDate() : snapshot.earlyEndDate.toDate()
            }))
            const cpmTask = cpmMap.get(taskId);
            if (cpmTask) {
                snapshots.push({
                    date: new Date(),
                    value: new Date(convertIndexToSeconds(cpmTask.ef, cpmTask.cal_id, calendars) * 1000)
                });
            }
            return snapshots;
        }
        return [];
    }, [taskSnapshots, taskId, activeProject])

    const regressionLine = useMemo(() => linearRegressionEndpoints(data), [data]);

    const dateScale = useMemo(() =>
            scaleTime({
                range: [margin.left, width - margin.right],
                domain: extent(data, d => d.date) as [Date, Date],
            }),
        [data])

    const yScale = useMemo(() =>
            scaleTime({
                range: [height - margin.bottom, margin.top],
                domain: extent([...data, {value: baselineDate}], d => d.value) as [Date, Date],
            }), [data])

    const midValue = useMemo(() => {
        if (!hoverPosition) return null;
        const date = dateScale.invert(hoverPosition.x);
        if (!date) return null;
        const index = bisectDate(data, date, 1);
        const d0 = data[index -1];
        const d1 = data[index];
        if (!d0 || !d1) return null;
        const xDelta = d1.date.getTime() - d0.date.getTime();
        const yDelta = d1.value.getTime() - d0.value.getTime();
        const midValue = yDelta === 0 ? d0.value : new Date(
            d0.value.getTime() + ((date.getTime() - d0.date.getTime()) * (yDelta / xDelta)));
        return {value: midValue, date: date};
    }, [hoverPosition, data])

    const numMonths = useMemo(() => {
        if (data.length < 2) return 1;
        const start = data[0].date;
        const end = data[data.length - 1].date;
        return moment(end).diff(moment(start), 'months', true) + 1;
    }, [data])

    const dataPopup = () => {
        if (midValue && midValue.date && midValue.value && hoverPosition) {
            return (
                <div style={{
                    position: 'absolute',
                    top: height + yScale(midValue.value) - 120,
                    left: hoverPosition.x -150,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    padding: '5px',
                    fontSize: '10px',
                    pointerEvents: 'none'
                }}>
                    {/*<div>Date: {midValue.date.toDateString()}</div>*/}
                    <strong>Forecast: {midValue.value.toDateString()}</strong>
                    <div style={{color: 'rgb(15, 157, 88)'}}>(baseline: {baselineDate.toDateString()})</div>
                </div>
            )
        }
        return null;
    }

    return <div>
        {data.length > 1 && activeProject && activeProject.projectId === task?.projectId &&
        <svg width={width} height={height}
             onMouseMove={(e) => setHoverPosition({x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY})}
                onMouseLeave={() => setHoverPosition(null)}
        >
            <rect
                x={0}
                y={0}
                width={width}
                height={height}
                fill="white"
            />
            <GridRows
                scale={yScale}
                width={width}
                strokeDasharray="1,3"
                stroke={'#484643'}
                strokeOpacity={0.8}
                pointerEvents="none"
                left={margin.left + 30}
                // top={margin.top}
                numTicks={numMonths}
            />
            <AxisLeft scale={yScale}
                      left={margin.left}
                      // top={margin.top}
                      stroke={accentColor}
                      strokeWidth={0}
                        tickLabelProps={() => ({
                            fontSize: 10,
                            textAnchor: 'start',
                            dy: '0.33em',
                        })}
                      numTicks={numMonths}
                      tickFormat={d => {
                          if (d instanceof Date) {
                            return moment(d).format('MMM YY');
                            } else {
                              return '' + d;
                          }
                      }}
            />
            <GridColumns
                top={margin.top}
                left={margin.left}
                scale={dateScale}
                height={height - 20}
                strokeDasharray="1,3"
                stroke={'#484643'}
                strokeOpacity={0.8}
                pointerEvents="none"
                numTicks={data.length}
            />
            <line
                x1={40}
                y1={yScale(baselineDate) ?? 0}
                x2={width}
                y2={yScale(baselineDate) ?? 0}
                stroke="rgb(15, 157, 88)"
                strokeWidth={3}
                strokeDasharray="8,3"
                opacity={0.5}
            />
            <LinePath
                data={data}
                x={d => dateScale(d.date) ?? 0}
                y={d => yScale(d.value) ?? 0}
                stroke={lineColor}
                strokeWidth={2}
            />
            {regressionLine && (
                <LinePath
                    data={regressionLine}
                    x={d => dateScale(d.date) ?? 0}
                    y={d => yScale(d.value) ?? 0}
                    stroke="red"
                    strokeWidth={2}
                    strokeDasharray="5,5"
                    opacity={0.5}
                />
            )}
            {data.map((d, i) => (
                <circle
                    key={i}
                    cx={dateScale(d.date)}
                    cy={yScale(d.value)}
                    r={4}
                    fill="white"
                    stroke={lineColor}
                    strokeWidth={2}
                />
            ))}
            {hoverPosition && midValue && <line
                x1={hoverPosition.x}
                y1={0}
                x2={hoverPosition.x}
                y2={height}
                stroke="gray"
                strokeWidth={1}
                strokeDasharray="3,3"
                pointerEvents="none"
            />}
            {hoverPosition && midValue && <circle
                cx={hoverPosition.x}
                cy={yScale(midValue.value)}
                r={4}
                fill={lineColor}
                stroke={lineColor}
                strokeWidth={2}
                pointerEvents="none"
            />}
            {hoverPosition && midValue && <circle
                cx={hoverPosition.x}
                cy={yScale(baselineDate)}
                r={4}
                fill="rgb(15, 157, 88)"
                stroke="rgb(15, 157, 88)"
                strokeWidth={2}
                pointerEvents="none"
            />}
        </svg>}
        {hoverPosition && midValue && <div style={{
            position: 'absolute',
            top: height + 140,
            left: hoverPosition.x - 45,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            padding: '5px',
            fontSize: '10px',
            pointerEvents: 'none',
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            {dateScale.invert(hoverPosition.x).toDateString()}
        </div>}
        {dataPopup()}
    </div>
}